import {
  CreateRng, SampleNormal, Mean, Variance, FiveNumberSummary, WelchTTest, MannWhitney, OneWayAnova, KruskalWallis,
  Pearson, Spearman, Kendall, LinearFit, LogisticFit, PoissonFit, ChiSquareIndependence,
  KaplanMeier, MedianSurvival, LogRank, type Rng, type FiveNumber, type SurvivalStep, type SurvivalSubject,
} from "../../../../lib/sampleStats";

export type AnalysisKey = "two-groups" | "multi-groups" | "correlation" | "regression" | "categorical" | "survival";
export type ResultRow = [label: string, value: string];

export const METHODS: Record<AnalysisKey, string[]> = {
  "two-groups": ["Welch t-test", "Mann-Whitney U"],
  "multi-groups": ["One-way ANOVA", "Kruskal-Wallis"],
  correlation: ["Pearson", "Spearman", "Kendall"],
  regression: ["Linear", "Logistic", "Poisson"],
  categorical: ["Chi-squared"],
  survival: ["Kaplan–Meier"],
};

export interface GroupData { values: number[]; summary: FiveNumber; mean: number }
export interface Point { x: number; y: number }

interface Base { rows: ResultRow[]; legend?: string[] }
export type Analysis = Base & (
  | { chart: "box"; groups: GroupData[] }
  | { chart: "scatter"; points: Point[]; curve?: (x: number) => number; xLabel: string; yLabel: string; yDomain?: [number, number] }
  | { chart: "bars"; categories: string[]; counts: number[][] }
  | { chart: "survival"; curves: [SurvivalStep[], SurvivalStep[]]; maxTime: number }
);

export function FormatP(p: number): string {
  return p < 0.001 ? "< 0.001" : p.toFixed(3);
}

const Repeat = <T,>(n: number, make: () => T): T[] => Array.from({ length: n }, make);
const BuildGroup = (values: number[]): GroupData => ({ values, summary: FiveNumberSummary(values), mean: Mean(values) });
const MeanSd = (v: number[]) => `${Mean(v).toFixed(1)} ± ${Math.sqrt(Variance(v)).toFixed(1)}`;
const GROUP_NAMES = ["Group A", "Group B", "Group C"];

function BuildTwoGroups(rng: Rng, method: number): Analysis {
  const a = Repeat(40, () => SampleNormal(rng, 52, 9)), b = Repeat(40, () => SampleNormal(rng, 58, 10));
  const groups = [BuildGroup(a), BuildGroup(b)];
  if (method === 1) {
    const { u, z, p } = MannWhitney(a, b);
    return { chart: "box", groups, legend: GROUP_NAMES.slice(0, 2), rows: [["Test", "Mann-Whitney U"], ["Group A median", groups[0].summary.median.toFixed(1)], ["Group B median", groups[1].summary.median.toFixed(1)], ["U", u.toFixed(0)], ["z", z.toFixed(2)], ["p-value", FormatP(p)]] };
  }
  const { t, df, p } = WelchTTest(a, b);
  return { chart: "box", groups, legend: GROUP_NAMES.slice(0, 2), rows: [["Test", "Welch t-test"], ["Group A mean ± SD", MeanSd(a)], ["Group B mean ± SD", MeanSd(b)], ["t", t.toFixed(2)], ["df", df.toFixed(1)], ["p-value", FormatP(p)]] };
}

function BuildMultiGroups(rng: Rng, method: number): Analysis {
  const samples = [50, 55, 58].map((m) => Repeat(30, () => SampleNormal(rng, m, 9)));
  const groups = samples.map(BuildGroup);
  const means: ResultRow[] = samples.map((s, i) => [`${GROUP_NAMES[i]} mean`, Mean(s).toFixed(1)]);
  if (method === 1) {
    const { h, df, p } = KruskalWallis(samples);
    return { chart: "box", groups, legend: GROUP_NAMES, rows: [["Test", "Kruskal-Wallis"], ...means, ["H", h.toFixed(2)], ["df", String(df)], ["p-value", FormatP(p)]] };
  }
  const { f, df1, df2, p } = OneWayAnova(samples);
  return { chart: "box", groups, legend: GROUP_NAMES, rows: [["Test", "One-way ANOVA"], ...means, ["F", f.toFixed(2)], ["df", `${df1}, ${df2}`], ["p-value", FormatP(p)]] };
}

// A monotonic but curved relationship, so the three coefficients visibly differ.
function BuildCorrelation(rng: Rng, method: number): Analysis {
  const points = Repeat(60, () => { const x = rng() * 20; return { x, y: 10 * Math.log(1 + x) + SampleNormal(rng, 0, 2.5) }; });
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  const base = { chart: "scatter" as const, points, xLabel: "Variable X", yLabel: "Variable Y" };
  if (method === 1) { const { rho, p } = Spearman(xs, ys); return { ...base, rows: [["Method", "Spearman (rank)"], ["ρ", rho.toFixed(3)], ["n", "60"], ["p-value", FormatP(p)]] }; }
  if (method === 2) { const { tau, p } = Kendall(xs, ys); return { ...base, rows: [["Method", "Kendall tau-b"], ["τ", tau.toFixed(3)], ["n", "60"], ["p-value", FormatP(p)]] }; }
  const { r, p } = Pearson(xs, ys);
  return { ...base, rows: [["Method", "Pearson (linear)"], ["r", r.toFixed(3)], ["n", "60"], ["p-value", FormatP(p)]] };
}

function SamplePoisson(rng: Rng, lambda: number): number {
  const limit = Math.exp(-lambda);
  let k = 0, prod = rng();
  while (prod > limit) { k++; prod *= rng(); }
  return k;
}

function BuildLinear(rng: Rng): Analysis {
  const points = Repeat(50, () => { const x = rng() * 10; return { x, y: 3 + 2.1 * x + SampleNormal(rng, 0, 3) }; });
  const fit = LinearFit(points.map((p) => p.x), points.map((p) => p.y));
  return {
    chart: "scatter", points, curve: (x) => fit.intercept + fit.slope * x, xLabel: "Predictor", yLabel: "Outcome", legend: ["Observations", "Fitted line"],
    rows: [["Model", "Linear (Gaussian)"], ["Slope", fit.slope.toFixed(2)], ["Intercept", fit.intercept.toFixed(2)], ["R²", fit.r2.toFixed(3)], ["n", "50"]],
  };
}

function BuildLogistic(rng: Rng): Analysis {
  const raw = Repeat(80, () => { const x = rng() * 10; return { x, y: rng() < 1 / (1 + Math.exp(-(-4 + 0.8 * x))) ? 1 : 0 }; });
  const fit = LogisticFit(raw.map((p) => p.x), raw.map((p) => p.y));
  // Small vertical jitter so stacked 0/1 outcomes stay visible; the fit uses the exact values.
  const points = raw.map((p, i) => ({ x: p.x, y: p.y + (((i * 37) % 11) - 5) * 0.006 }));
  return {
    chart: "scatter", points, curve: (x) => 1 / (1 + Math.exp(-(fit.intercept + fit.slope * x))), xLabel: "Predictor", yLabel: "Probability of outcome", yDomain: [-0.05, 1.05], legend: ["Observations (0 or 1)", "Fitted probability"],
    rows: [["Model", "Logistic (binomial)"], ["Coefficient", fit.slope.toFixed(3)], ["Odds ratio per unit", Math.exp(fit.slope).toFixed(2)], ["p-value", FormatP(fit.p)], ["n", "80"]],
  };
}

function BuildPoisson(rng: Rng): Analysis {
  const points = Repeat(60, () => { const x = rng() * 10; return { x, y: SamplePoisson(rng, Math.exp(0.3 + 0.2 * x)) }; });
  const fit = PoissonFit(points.map((p) => p.x), points.map((p) => p.y));
  return {
    chart: "scatter", points, curve: (x) => Math.exp(fit.intercept + fit.slope * x), xLabel: "Predictor", yLabel: "Count", legend: ["Observed counts", "Fitted rate"],
    rows: [["Model", "Poisson (log link)"], ["Coefficient", fit.slope.toFixed(3)], ["Rate ratio per unit", Math.exp(fit.slope).toFixed(2)], ["p-value", FormatP(fit.p)], ["n", "60"]],
  };
}

function SampleCategory(rng: Rng, probs: number[]): number {
  let u = rng();
  for (let i = 0; i < probs.length; i++) { u -= probs[i]; if (u <= 0) return i; }
  return probs.length - 1;
}

function BuildCategorical(rng: Rng): Analysis {
  const counts = [[0.5, 0.3, 0.2], [0.3, 0.35, 0.35]].map((probs) => {
    const row = [0, 0, 0];
    for (let i = 0; i < 120; i++) row[SampleCategory(rng, probs)]++;
    return row;
  });
  const { chi, df, p } = ChiSquareIndependence(counts);
  return {
    chart: "bars", categories: ["Low", "Medium", "High"], counts, legend: GROUP_NAMES.slice(0, 2),
    rows: [["Test", "Chi-squared (independence)"], ["χ²", chi.toFixed(2)], ["df", String(df)], ["p-value", FormatP(p)], ["n", "240"]],
  };
}

function SampleSubjects(rng: Rng, meanTime: number): SurvivalSubject[] {
  return Repeat(60, () => {
    const time = -Math.log(Math.max(rng(), 1e-12)) * meanTime;
    const censor = 6 + rng() * 30;
    return { time: Math.round(Math.min(time, censor) * 10) / 10, event: time <= censor };
  });
}

function FormatMedian(steps: SurvivalStep[]): string {
  const m = MedianSurvival(steps);
  return m === null ? "Not reached" : `${m.toFixed(1)} months`;
}

function BuildSurvival(rng: Rng): Analysis {
  const a = SampleSubjects(rng, 14), b = SampleSubjects(rng, 24);
  const curves: [SurvivalStep[], SurvivalStep[]] = [KaplanMeier(a), KaplanMeier(b)];
  const { chi, p } = LogRank(a, b);
  return {
    chart: "survival", curves, maxTime: 36, legend: GROUP_NAMES.slice(0, 2),
    rows: [["Estimator", "Kaplan–Meier"], ["Group A median", FormatMedian(curves[0])], ["Group B median", FormatMedian(curves[1])], ["Log-rank χ²", chi.toFixed(2)], ["p-value", FormatP(p)]],
  };
}

const BUILDERS: Record<AnalysisKey, (rng: Rng, method: number) => Analysis> = {
  "two-groups": BuildTwoGroups,
  "multi-groups": BuildMultiGroups,
  correlation: BuildCorrelation,
  regression: (rng, method) => [BuildLinear, BuildLogistic, BuildPoisson][method](rng),
  categorical: BuildCategorical,
  survival: BuildSurvival,
};

export function BuildAnalysis(key: AnalysisKey, method: number, seed: number): Analysis {
  return BUILDERS[key](CreateRng(seed), method);
}
